import { MouseEvent as ReactMouseEvent, useCallback, useRef } from 'react'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import { ElementListItem } from '../types'

export type SelectionModifier = 'none' | 'toggle' | 'range'

interface ComputeNextSelectionArgs {
  id: string
  /** Ids in display order — multi-select range follows this order. */
  orderedIds: string[]
  selectedIds: string[]
  /** Anchor for multi-select range (last clicked row). */
  anchorId: string | null
  modifier: SelectionModifier
}

/** Pure selection reducer — kept side-effect free so it can be unit tested without React. */
export const computeNextSelection = ({
  id,
  orderedIds,
  selectedIds,
  anchorId,
  modifier,
}: ComputeNextSelectionArgs): string[] => {
  if (modifier === 'none') return [id]

  if (modifier === 'toggle') {
    return selectedIds.includes(id)
      ? selectedIds.filter(selected => selected !== id)
      : [...selectedIds, id]
  }

  // range
  const startIndex = anchorId ? orderedIds.indexOf(anchorId) : -1
  const endIndex = orderedIds.indexOf(id)
  if (startIndex === -1 || endIndex === -1) {
    return Array.from(new Set([...selectedIds, id]))
  }
  const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
  return Array.from(new Set([...selectedIds, ...orderedIds.slice(from, to + 1)]))
}

const modifierFromEvent = (event: ReactMouseEvent<HTMLElement>): SelectionModifier => {
  if (eventMatchesPointerModifier(event, 'multiSelect')) return 'range'
  if (eventMatchesPointerModifier(event, 'listToggle')) return 'toggle'
  return 'none'
}

interface UseListMultiSelectArgs {
  items: ElementListItem[]
  selectedIds: string[]
  onSelectionChange?: (ids: string[]) => void
}

interface UseListMultiSelectResult {
  handleRowClick: (id: string, event: ReactMouseEvent<HTMLElement>) => void
}

/**
 * Row-click selection for the element list: plain click = single select, list-toggle modifier =
 * toggle, multi-select modifier = range. Range stays within the active tab's items, so a selection
 * is always one element type. When no onSelectionChange is given the list is display-only (clicks
 * do nothing).
 */
const useListMultiSelect = ({
  items,
  selectedIds,
  onSelectionChange,
}: UseListMultiSelectArgs): UseListMultiSelectResult => {
  const anchorRef = useRef<string | null>(selectedIds[selectedIds.length - 1] ?? null)

  const handleRowClick = useCallback(
    (id: string, event: ReactMouseEvent<HTMLElement>) => {
      if (!onSelectionChange) return
      const nextSelectedIds = computeNextSelection({
        id,
        orderedIds: items.map(item => item.id),
        selectedIds,
        anchorId: anchorRef.current,
        modifier: modifierFromEvent(event),
      })
      anchorRef.current = id
      onSelectionChange(nextSelectedIds)
    },
    [items, selectedIds, onSelectionChange],
  )

  return { handleRowClick }
}

export default useListMultiSelect
